/** BOM audit (plant manager) — recipe-abstract walk sessions. */

export type MfgBomAuditKind = "planned_correctness" | "build_conformance";
export type MfgBomAuditSessionStatus = "open" | "partial" | "completed" | "abandoned";
export type MfgBomAuditNodeResult = "unreviewed" | "ok" | "issue" | "skipped";
export type MfgBomAuditNodeKind =
  | "recipe_line"
  | "material_use_line"
  | "named_variant_application"
  | "material_use_member"
  | "variant_effect_line";

export type MfgBomAuditSession = {
  id: string;
  organization_id: string;
  plant_id: string | null;
  recipe_id: string;
  parent_session_id: string | null;
  audit_kind: MfgBomAuditKind;
  status: MfgBomAuditSessionStatus;
  opened_by_user_id: string | null;
  last_audited_by_user_id: string | null;
  last_audited_at: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
};

export type MfgBomAuditNode = {
  id: string;
  organization_id: string;
  session_id: string;
  parent_node_id: string | null;
  node_kind: MfgBomAuditNodeKind;
  subject_id: string | null;
  subject_recipe_id: string | null;
  display_label: string;
  quantity: number | null;
  sort_order: number;
  result: MfgBomAuditNodeResult;
  notes: string;
  sub_audit_session_id: string | null;
  created_at: string;
  updated_at: string;
};

export type MfgBomAuditSessionsListResponse = {
  ok: true;
  organization_id: string;
  sessions: MfgBomAuditSession[];
};

export type MfgBomAuditSessionDetailResponse = {
  ok: true;
  session: MfgBomAuditSession;
  nodes: MfgBomAuditNode[];
};

export type MfgBomAuditSessionOpenResponse = MfgBomAuditSessionDetailResponse;

export type MfgBomAuditNodeSaveResponse = {
  ok: true;
  session: MfgBomAuditSession;
  node: MfgBomAuditNode;
};

export type MfgBomAuditStartSubAuditResponse = {
  ok: true;
  parent_session: MfgBomAuditSession;
  parent_node: MfgBomAuditNode;
  child_session: MfgBomAuditSession;
  child_nodes: MfgBomAuditNode[];
};

export type MfgBomAuditSessionAbandonResponse = {
  ok: true;
  session: MfgBomAuditSession;
};

export function isBomAuditNodeDrillable(node: MfgBomAuditNode): boolean {
  if (node.sub_audit_session_id) return false;
  if (node.node_kind === "named_variant_application") return true;
  if (node.node_kind === "material_use_line") return true;
  return node.node_kind === "recipe_line" && Boolean(node.subject_recipe_id);
}

export const MFG_BOM_AUDIT_KIND_LABELS: Record<MfgBomAuditKind, string> = {
  planned_correctness: "Audit a parts list for accuracy",
  build_conformance: "Audit an active build for correct parts usage",
};

export const MFG_BOM_AUDIT_RESULT_LABELS: Record<MfgBomAuditNodeResult, string> = {
  unreviewed: "Unreviewed",
  ok: "OK",
  issue: "Issue",
  skipped: "Skipped",
};
